import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';

import { CHANNEL_RECORDING_TASK_ACTION_TO_NOTIFICATION_DATA } from '../constants';
import { ChannelRecordingTaskActionEnum, ChannelRecordingTaskType } from '../types';

import { channelRecordingTasksKeys } from './query-keys';

export type TUpdateChannelRecordingTaskActionParams = {
  id: string;
  action: ChannelRecordingTaskActionEnum;
};

async function updateChannelRecordingTaskACtion({
  id,
  action,
}: TUpdateChannelRecordingTaskActionParams): Promise<ChannelRecordingTaskType> {
  return mediaManagerClient.post(`/api/v1/channel_recording_tasks/${id}/${action}`);
}

type MutationFn = typeof updateChannelRecordingTaskACtion;

interface UseUpdateChannelRecordingTaskActionParams {
  config?: MutationConfig<MutationFn>;
}

export function useUpdateChannelRecordingTaskAction({
  config,
}: UseUpdateChannelRecordingTaskActionParams = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { addErrorNotification, addSuccessNotification } = useNotificationStore();

  return useMutation({
    onError(error, variables) {
      const notification = CHANNEL_RECORDING_TASK_ACTION_TO_NOTIFICATION_DATA[variables.action];

      if (notification) {
        addErrorNotification(t(notification.error.title), t(notification.error.description));
      }

      console.error(error);
    },
    onSuccess(_, variables) {
      const notification = CHANNEL_RECORDING_TASK_ACTION_TO_NOTIFICATION_DATA[variables.action];

      if (notification) {
        addSuccessNotification(t(notification.success.title), t(notification.success.description));
      }

      queryClient.invalidateQueries({ queryKey: channelRecordingTasksKeys.all });
    },
    ...config,
    mutationFn: updateChannelRecordingTaskACtion,
  });
}
